import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { AiIcon } from '../../AiIcon';
import {
  ItemContainer,
  ItemTitle,
  MoreButton,
  RenameContainer,
  RenameInput
} from './styles';

export interface HistoryItemProps {
  title: string;
  selected: boolean;
  menuOpen: boolean;
  renaming: boolean;
  onSelect: () => void;
  onOpenMenu: (anchor: HTMLElement) => void;
  onSubmitRename: (name: string) => void;
  onCancelRename: () => void;
}

/** "Chat AI / Sidebar / History / Item" do Figma. */
export const HistoryItem: FC<HistoryItemProps> = ({
  title,
  selected,
  menuOpen,
  renaming,
  onSelect,
  onOpenMenu,
  onSubmitRename,
  onCancelRename
}) => {
  const { t } = useTranslation('activities');
  const [draft, setDraft] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming) {
      setDraft(title);
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renaming, title]);

  if (renaming) {
    const submit = () => {
      const name = draft.trim();
      if (!name || name === title) {
        onCancelRename();
        return;
      }
      onSubmitRename(name);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        submit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onCancelRename();
      }
    };

    return (
      <RenameContainer>
        <RenameInput
          ref={inputRef}
          value={draft}
          aria-label={t('aiChat.rename')}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            setDraft(event.target.value)
          }
          onKeyDown={handleKeyDown}
          onBlur={submit}
        />
      </RenameContainer>
    );
  }

  const handleMoreClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onOpenMenu(event.currentTarget);
  };

  return (
    <ItemContainer
      role="button"
      tabIndex={0}
      aria-current={selected ? 'true' : undefined}
      $selected={selected}
      $menuOpen={menuOpen}
      onClick={onSelect}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (
          event.target === event.currentTarget &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          event.preventDefault();
          onSelect();
        }
      }}
    >
      <ItemTitle title={title}>{title}</ItemTitle>
      <MoreButton
        type="button"
        aria-label={t('aiChat.moreActions')}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        $open={menuOpen}
        onClick={handleMoreClick}
      >
        <AiIcon name="more" />
      </MoreButton>
    </ItemContainer>
  );
};
